import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { EyeOff, PauseCircle, Trash2, ShieldCheck } from 'lucide-react';
import { SCShell, SCHeading, goldBtn, ghostBtn, RequireAuth } from './sc/SCShared';
import { scGetProfile, scUpdateProfile, scDisable, scDeleteProfile } from '../api';

const Inner = () => {
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [visibility, setVisibility] = useState('public');
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    scGetProfile().then((data) => {
      if (!data || data.age_verification_status !== 'verified') { navigate('/sacred-connections/onboarding'); return; }
      setP(data); setVisibility(data.profile_visibility || 'public');
    }).catch(() => navigate('/sacred-connections'));
  }, [navigate]);

  const saveVisibility = async () => {
    try { await scUpdateProfile({ profile_visibility: visibility }); toast.success('Privacy updated.'); } catch { toast.error('Could not update.'); }
  };
  const disable = async () => { try { await scDisable(); toast.success('Dating profile disabled. You can reactivate anytime.'); navigate('/sacred-connections'); } catch { toast.error('Could not disable.'); } };
  const del = async () => { try { await scDeleteProfile(); toast.success('Your dating profile was deleted. Your shopping account is unaffected.'); navigate('/sacred-connections'); } catch { toast.error('Could not delete.'); } };

  if (!p) return <SCShell><p className="font-cormorant text-2xl text-[#e9d8ff]/60 animate-pulse">Loading…</p></SCShell>;

  return (
    <SCShell>
      <SCHeading eyebrow="Settings" title="Privacy & preferences" sub="You are always in control of your Sacred Connections presence." />
      <div className="max-w-lg space-y-6">
        <div className="rounded-2xl p-6 border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md">
          <div className="flex items-center gap-2 mb-4"><EyeOff className="h-5 w-5 text-[#d946ef]" /><h3 className="font-cinzel text-base text-[#f5d786]">Profile visibility</h3></div>
          <select data-testid="sc-set-visibility" value={visibility} onChange={(e) => setVisibility(e.target.value)} className="w-full bg-black/30 border border-[#8b5cf6]/30 rounded-xl px-4 py-3 text-[#e9d8ff] focus:outline-none focus:border-[#d946ef]">
            <option value="public">Public to Sacred Connections users</option>
            <option value="matches_only">Visible only to matches</option>
            <option value="incognito">Incognito (discoverable when you like someone)</option>
            <option value="hidden">Hidden</option>
          </select>
          <button onClick={saveVisibility} className={`${goldBtn} mt-4`} data-testid="sc-set-save">Save privacy</button>
        </div>

        <div className="rounded-2xl p-6 border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md">
          <div className="flex items-center gap-2 mb-3"><PauseCircle className="h-5 w-5 text-[#f5d786]" /><h3 className="font-cinzel text-base text-[#f5d786]">Pause dating</h3></div>
          <p className="font-jost text-sm text-[#e9d8ff]/60 mb-4">Hide your profile from discovery and matching. You can reactivate anytime — nothing is deleted.</p>
          <button onClick={disable} className={ghostBtn} data-testid="sc-set-disable">Disable my dating profile</button>
        </div>

        <div className="rounded-2xl p-6 border border-[#ff6b9d]/30 bg-[#ff6b9d]/[0.05]">
          <div className="flex items-center gap-2 mb-3"><Trash2 className="h-5 w-5 text-[#ff6b9d]" /><h3 className="font-cinzel text-base text-[#ff6b9d]">Delete dating profile</h3></div>
          <p className="font-jost text-sm text-[#e9d8ff]/60 mb-4 flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-[#8b5cf6] shrink-0 mt-0.5" /> This removes your Sacred Connections profile, likes and matches. Your Prestige Elements shopping account, orders and rewards are <strong className="text-[#e9d8ff]">not</strong> affected.</p>
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)} className="rounded-full px-6 py-3 text-sm border border-[#ff6b9d]/50 text-[#ff6b9d] hover:bg-[#ff6b9d]/10" data-testid="sc-set-delete">Delete my dating profile</button>
          ) : (
            <div className="flex gap-3">
              <button onClick={del} className="rounded-full px-6 py-3 text-sm bg-[#ff6b9d] text-black" data-testid="sc-set-delete-confirm">Yes, delete permanently</button>
              <button onClick={() => setConfirmDelete(false)} className={ghostBtn}>Cancel</button>
            </div>
          )}
        </div>
      </div>
    </SCShell>
  );
};

const SCSettings = () => <RequireAuth><Inner /></RequireAuth>;
export default SCSettings;
